<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="pb-3 d-flex align-center flex-wrap">
        <span class="text-h6">MSC Reports</span>
        <v-spacer />
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn outlined color="primary" v-bind="attrs" v-on="on">
              Download
              <v-icon right small color="primary">mdi-download</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="downloadAsCsv">
              <v-list-item-title>Download as CSV</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadAsXls">
              <v-list-item-title>Download as XLS</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-0" />
        <div class="msc-ag-grid-wrap px-2 pt-2 pb-2">
          <ag-grid-vue
            class="ag-theme-alpine"
            style="width: 100%; height: 70vh;"
            :column-defs="columnDefs"
            :row-data="rowData"
            :default-col-def="defaultColDef"
            :context="gridContext"
            :get-row-id="getRowId"
            :process-data-from-clipboard="processClipboard"
            :single-click-edit="true"
            :stop-editing-when-cells-lose-focus="false"
            :enable-cell-text-selection="true"
            :suppress-row-click-selection="true"
            row-selection="single"
            @grid-ready="onGridReady"
            @cell-value-changed="onCellValueChanged"
          />
        </div>
        <p class="px-4 pb-2 mb-0 text-caption grey--text">
          Most columns are editable except Job Number (assigned on Save for new rows).
          Required to save a new row: Vessel Name, PIC, ETA, and ETD (use the date picker; dd-mm-yyyy HH:mm).
          Paste from Excel/Sheets into Vessel Name, PIC, ETA, and ETD (tab-separated).
          Extra blank rows are added automatically as you fill in rows near the bottom.
        </p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';

import MscReportJobLinkCell from './cells/MscReportJobLinkCell';
import MscReportActionsCell from './cells/MscReportActionsCell';
import MscReportSelectCellEditor from './cells/MscReportSelectCellEditor';
import MscReportDateTimeCellEditor from './cells/MscReportDateTimeCellEditor';

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const MSC_BILLING_PARTY_NAME = 'Mediterranean Shipping Company';
const MSC_SUPPLIER_SERVICE_LOADS = [
  { optionsKey: 'sscecSuppliers', serviceName: 'SSCEC' },
  { optionsKey: 'garbageDisposalSuppliers', serviceName: 'Garbage Disposal' },
  { optionsKey: 'hotelSignOnSuppliers', serviceName: 'Hotel Booking Crew Sign On' },
  { optionsKey: 'hotelSignOffSuppliers', serviceName: 'Hotel Booking Crew Sign Off' },
  { optionsKey: 'transportServiceSuppliers', serviceName: 'Transport Service' },
];

const PASTE_EDITABLE_FIELDS = ['vessel_name', 'pic', 'eta', 'etd'];
const MIN_TRAILING_BLANK_ROWS = 5;
const EXPORT_COLUMNS = [
  { key: 'job_name', title: 'Job Number' },
  { key: 'vessel_name', title: 'Vessel Name' },
  { key: 'pic', title: 'PIC' },
  { key: 'immigration', title: 'Immigration' },
  { key: 'port_clearance_time', title: 'Port Clearance Time' },
  { key: 'eta', title: 'ETA' },
  { key: 'berthing', title: 'Berthing' },
  { key: 'etd', title: 'ETD' },
  { key: 'location', title: 'Location' },
  { key: 'ctm', title: 'CTM' },
  { key: 'crew_change', title: 'Crew Change' },
  { key: 'sscec_supplier', title: 'SSCEC' },
  { key: 'garbage_disposal_supplier', title: 'Garbage Disposal' },
  { key: 'hotel_booking_crew_sign_on_supplier', title: 'Hotel Booking Crew Sign On' },
  { key: 'hotel_booking_crew_sign_off_supplier', title: 'Hotel Booking Crew Sign Off' },
  { key: 'transport_service_supplier', title: 'Transport Service' },
  { key: 'others_suppliers', title: 'Others' },
  { key: 'remarks_arrival', title: 'Arrival Remarks' },
  { key: 'remarks_departure', title: 'Departure Remarks' },
  { key: 'po_number', title: 'PO Number' },
];

const multilineCellStyle = { whiteSpace: 'pre-line', lineHeight: '1.35' };

const editableTextColDef = (headerName, field, minWidth = 140, multiline = false) => ({
  headerName,
  field,
  editable: true,
  minWidth,
  ...(multiline
    ? {
        cellStyle: multilineCellStyle,
        wrapText: true,
        autoHeight: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { rows: 4, cols: 40, maxLength: 2000 },
      }
    : {}),
});

const dateTimeColDef = (headerName, field, minWidth = 150) => ({
  headerName,
  field,
  editable: true,
  minWidth,
  cellEditor: 'mscReportDateTimeCellEditor',
  suppressKeyboardEvent: (params) => {
    const { event } = params;
    if (!event || event.metaKey || event.ctrlKey || event.altKey) return false;
    return event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete';
  },
});

const selectColDef = (headerName, field, optionsKey, minWidth = 140, multiple = false) => ({
  headerName,
  field,
  editable: true,
  minWidth,
  cellEditor: 'mscReportSelectCellEditor',
  cellEditorParams: { optionsKey, multiple },
  ...(multiple
    ? { cellStyle: multilineCellStyle, wrapText: true, autoHeight: true }
    : {}),
});

export default {
  name: 's-msc-reports',
  components: {
    AgGridVue,
    // Must be on this parent so ag-grid-vue can Vue.extend() them by name.
    mscReportJobLinkCell: MscReportJobLinkCell,
    mscReportActionsCell: MscReportActionsCell,
    mscReportSelectCellEditor: MscReportSelectCellEditor,
    mscReportDateTimeCellEditor: MscReportDateTimeCellEditor,
  },
  data() {
    return {
      loading: false,
      savingRows: {},
      completedLoading: null,
      rowData: [],
      gridApi: null,
      usersByName: {},
      vesselsByName: {},
      mscSelectOptions: {
        mscVessels: [],
        picUsers: [],
        sscecSuppliers: [],
        garbageDisposalSuppliers: [],
        hotelSignOnSuppliers: [],
        hotelSignOffSuppliers: [],
        transportServiceSuppliers: [],
      },
      mscEditorOptionsLoaded: false,
      defaultColDef: {
        resizable: true,
        sortable: true,
        suppressMovable: true,
        minWidth: 100,
      },
    };
  },
  computed: {
    gridContext() {
      return { componentParent: this };
    },
    showPoNumberColumn() {
      return this.rowData.some(
        (row) => !this.isBlankRow(row) && this.isPoNumberBillingReady(row)
      );
    },
    columnDefs() {
      const cols = [
        {
          headerName: 'Job Number',
          field: 'job_name',
          cellRenderer: 'mscReportJobLinkCell',
          editable: false,
          minWidth: 120,
          pinned: 'left',
        },
        selectColDef('Vessel Name', 'vessel_name', 'mscVessels', 180),
        selectColDef('PIC', 'pic', 'picUsers', 140),
        editableTextColDef('Immigration', 'immigration', 160, true),
        dateTimeColDef('Port Clearance Time', 'port_clearance_time', 170),
        dateTimeColDef('ETA', 'eta', 170),
        dateTimeColDef('Berthing', 'berthing', 170),
        dateTimeColDef('ETD', 'etd', 170),
        editableTextColDef('Location', 'location', 100),
        editableTextColDef('CTM', 'ctm', 140, true),
        editableTextColDef('Crew Change', 'crew_change', 120, true),
        selectColDef('SSCEC', 'sscec_supplier', 'sscecSuppliers', 140),
        selectColDef('Garbage Disposal', 'garbage_disposal_supplier', 'garbageDisposalSuppliers', 160),
        selectColDef('Hotel Booking Crew Sign On', 'hotel_booking_crew_sign_on_supplier', 'hotelSignOnSuppliers', 200, true),
        selectColDef('Hotel Booking Crew Sign Off', 'hotel_booking_crew_sign_off_supplier', 'hotelSignOffSuppliers', 200, true),
        selectColDef('Transport Service', 'transport_service_supplier', 'transportServiceSuppliers', 160, true),
        editableTextColDef('Others', 'others_suppliers', 140, true),
        editableTextColDef('Arrival Remarks', 'remarks_arrival', 180),
        editableTextColDef('Departure Remarks', 'remarks_departure', 180),
      ];

      if (this.showPoNumberColumn) {
        cols.push({
          ...editableTextColDef('PO Number', 'po_number', 110),
          editable: (params) => this.isPoNumberBillingReady(params.data),
        });
      }

      cols.push({
          headerName: 'Actions',
          field: '_actions',
          editable: false,
          cellRenderer: 'mscReportActionsCell',
          minWidth: 72,
          maxWidth: 80,
          width: 72,
          pinned: 'right',
          sortable: false,
          suppressSizeToFit: true,
        });

      return cols;
    },
  },
  methods: {
    getRowId(params) {
      const data = params.data || {};
      return String(data._gridRowKey || data.operation_id);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
    },
    onCellValueChanged() {
      this.ensureTrailingBlankRows();
    },
    isBlankRow(row) {
      return row && String(row.operation_id).startsWith('new-');
    },
    blankRowHasContent(row) {
      if (!row) return false;
      const skipKeys = new Set([
        'operation_id',
        '_gridRowKey',
        'job_name',
        'is_draft',
        'approval_closure',
        'status',
        'billing_party_id',
      ]);
      return Object.keys(row).some((key) => {
        if (skipKeys.has(key)) return false;
        const value = String(row[key] ?? '').trim();
        return value && value !== '-';
      });
    },
    normalizeMscDisplayValue(val) {
      const s = String(val ?? '').trim();
      return s === '-' ? '' : s;
    },
    normalizeMscDisplayRow(row) {
      const normalized = { ...row };
      Object.keys(normalized).forEach((key) => {
        if (typeof normalized[key] === 'string') {
          normalized[key] = this.normalizeMscDisplayValue(normalized[key]);
        }
      });
      return normalized;
    },
    exportRows() {
      return this.rowData.filter((row) => {
        const isBlankNew = String(row.operation_id).startsWith('new-');
        const hasContent = row.vessel_name || row.pic || row.eta || row.etd;
        return !isBlankNew || hasContent;
      });
    },
    getExportColumns() {
      const columns = EXPORT_COLUMNS.filter(
        (col) => col.key !== 'po_number' || this.showPoNumberColumn
      );
      return {
        keys: columns.map((c) => c.key),
        titles: columns.map((c) => c.title),
      };
    },
    normalizeJobStatus(status) {
      if (status === 'Billing' || status === 2) return 'Billing';
      if (status === 'Completed' || status === 4) return 'Completed';
      return String(status ?? '');
    },
    isPoNumberBillingReady(row) {
      if (!row || this.isBlankRow(row)) return false;
      const status = this.normalizeJobStatus(row.status);
      return status === 'Billing' || status === 'Completed';
    },
    getRowFieldValue(rowKey, field, row) {
      if (this.gridApi) {
        const rowNode = this.gridApi.getRowNode(rowKey);
        const column = this.gridApi.getColumn(field);
        if (rowNode && column) {
          const value = this.gridApi.getValue(column, rowNode);
          if (value != null) {
            const trimmed = String(value).trim();
            if (trimmed && trimmed !== '-') return trimmed;
          }
        }
      }
      const fallback = row && row[field];
      if (fallback == null) return '';
      const trimmed = String(fallback).trim();
      return trimmed === '-' ? '' : trimmed;
    },
    isRowSaving(row) {
      if (!row) return false;
      const rowKey = String(row._gridRowKey || row.operation_id);
      return this.savingRows[rowKey] === true;
    },
    isRowCompleting(row) {
      return row && this.completedLoading === row.operation_id;
    },
    canShowMarkAsBilled(item) {
      const canMarkJobComplete = ['admin', 'accounts'].includes(this.$store.state.auth.profile.role);
      return Boolean(
        item &&
        !item.is_draft &&
        item.approval_closure &&
        item.status === 'Billing' &&
        canMarkJobComplete &&
        !String(item.operation_id).startsWith('new-')
      );
    },
    processClipboard(params) {
      const api = params.api;
      const focused = api.getFocusedCell();
      if (!focused || !params.data || !params.data.length) {
        return params.data;
      }

      const colId = focused.column.getColId();
      let startFieldIndex = PASTE_EDITABLE_FIELDS.indexOf(colId);
      if (startFieldIndex < 0) startFieldIndex = 0;

      const startRowIndex = focused.rowIndex;
      const needed = startRowIndex + params.data.length - this.rowData.length;
      if (needed > 0) {
        this.rowData.push(...this.blankMscRows(needed));
        api.setGridOption('rowData', this.rowData);
      }

      params.data.forEach((line, rowOffset) => {
        const row = this.rowData[startRowIndex + rowOffset];
        if (!row) return;
        line.forEach((cell, colOffset) => {
          const field = PASTE_EDITABLE_FIELDS[startFieldIndex + colOffset];
          if (!field) return;
          const value = cell == null ? '' : String(cell).trim();
          if (field === 'eta' || field === 'etd') {
            row[field] = this.normalizeDateCell(value);
          } else {
            row[field] = value;
          }
        });
      });

      api.refreshCells({ force: true });
      return null;
    },
    markJobComplete(item) {
      if (!this.canShowMarkAsBilled(item)) return;
      if (!item.billing_party_id) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: `Please assign a Billing Party to complete the job#${item.job_name}!`,
        });
        return;
      }

      if (!confirm('Completed jobs cannot be edited, Are you sure to complete this job?')) return;

      this.completedLoading = item.operation_id;
      this.$api.operations.completeJob(item.operation_id)
        .then(() => {
          this.$store.dispatch('setAlert', {
            show: true,
            message: `Job#${item.job_name} marked as completed`,
          });
          this.getReports();
        })
        .catch((err) => {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
        })
        .finally(() => {
          this.completedLoading = null;
          if (this.gridApi) {
            this.gridApi.refreshCells({ columns: ['_actions'], force: true });
          }
        });
    },
    escapeCsvField(val) {
      const s = val == null ? '' : String(val).replace(/\r\n/g, '\n');
      if (/[",\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
    escapeHtmlCell(val) {
      const s = val == null ? '' : String(val);
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    exportDateStamp() {
      return new Date().toISOString().split('T')[0];
    },
    triggerBlobDownload(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadAsCsv() {
      const { keys, titles } = this.getExportColumns();
      const rows = this.exportRows();
      const lines = [
        titles.map((t) => this.escapeCsvField(t)).join(','),
        ...rows.map((item) => keys.map((k) => this.escapeCsvField(item[k])).join(',')),
      ];
      const bom = '\uFEFF';
      const blob = new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      this.triggerBlobDownload(blob, `msc_reports_${this.exportDateStamp()}.csv`);
    },
    downloadAsXls() {
      const { keys, titles } = this.getExportColumns();
      const rows = this.exportRows();
      let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8" /></head><body><table border="1">';
      html += `<tr>${titles.map((t) => `<th>${this.escapeHtmlCell(t)}</th>`).join('')}</tr>`;
      rows.forEach((item) => {
        html += `<tr>${keys.map((k) => `<td>${this.escapeHtmlCell(item[k])}</td>`).join('')}</tr>`;
      });
      html += '</table></body></html>';
      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      this.triggerBlobDownload(blob, `msc_reports_${this.exportDateStamp()}.xls`);
    },
    jobNumberClick(item) {
      if (!item || !item.operation_id || String(item.operation_id).startsWith('new-')) return;
      this.$router.push({
        name: 'port calls',
        query: {
          jobStatus: 'All',
          openOperationId: String(item.operation_id),
          search: item.job_name || '',
          openFromMsc: '1',
        },
      });
    },
    withGridRowKey(row) {
      if (!row._gridRowKey) {
        row._gridRowKey = `op-${row.operation_id}`;
      }
      return row;
    },
    setRowDataFromApi(rows) {
      const dataRows = (Array.isArray(rows) ? rows : [])
        .map((row) => this.withGridRowKey(this.normalizeMscDisplayRow({ ...row })));
      const trailingBlanks = this.blankMscRows(
        dataRows.length ? MIN_TRAILING_BLANK_ROWS : MIN_TRAILING_BLANK_ROWS * 2
      );
      this.rowData = [...dataRows, ...trailingBlanks];
      if (this.gridApi) {
        this.gridApi.setGridOption('rowData', this.rowData);
      }
    },
    getReports() {
      this.loading = true;
      this.loadLookupData();
      this.$api.reports.getMscReports()
        .then((response) => {
          const rows = Array.isArray(response.msc_reports) ? response.msc_reports : [];
          this.setRowDataFromApi(rows);
        })
        .catch((err) => {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
          this.setRowDataFromApi([]);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    blankMscRows(count = 10) {
      const base = Date.now();
      return Array.from({ length: count }, (_, idx) => {
        const rowKey = `new-${base}-${idx + 1}`;
        return {
        operation_id: rowKey,
        _gridRowKey: rowKey,
        job_name: '',
        vessel_name: '',
        pic: '',
        port_clearance_time: '',
        eta: '',
        berthing: '',
        etd: '',
        location: '',
        ctm: '',
        crew_change: '',
        immigration: '',
        po_number: '',
        sscec_supplier: '',
        garbage_disposal_supplier: '',
        hotel_booking_crew_sign_on_supplier: '',
        hotel_booking_crew_sign_off_supplier: '',
        transport_service_supplier: '',
        others_suppliers: '',
        remarks_arrival: '',
        remarks_departure: '',
        is_draft: false,
        approval_closure: false,
        status: 'Open',
      };
      });
    },
    normalizeLookupKey(value) {
      return String(value || '').trim().toLowerCase();
    },
    vesselHasMscBillingParty(vessel) {
      const parties = vessel && vessel.card_id;
      if (!Array.isArray(parties)) return false;
      return parties.some((c) => c && c.name === MSC_BILLING_PARTY_NAME);
    },
    getMscSelectOptions(optionsKey) {
      return this.mscSelectOptions[optionsKey] || [];
    },
    loadLookupData() {
      if (this.mscEditorOptionsLoaded) return;
      const supplierRequests = MSC_SUPPLIER_SERVICE_LOADS.map(
        ({ serviceName }) => this.$api.service_catalogs.get_suppliers_list(serviceName)
      );
      Promise.all([
        this.$api.users.index(),
        this.$api.vessels.list({ mode: 'dropdown', per_page: 5000, page: 1 }),
        ...supplierRequests,
      ]).then((results) => {
        const usersRes = results[0];
        const vesselsRes = results[1];
        const supplierResults = results.slice(2);

        const users = (usersRes.users || []).filter((u) => u.is_active !== false);
        const mscVessels = (vesselsRes.vessels || []).filter((v) => this.vesselHasMscBillingParty(v));

        this.usersByName = {};
        this.vesselsByName = {};
        users.forEach((u) => {
          this.usersByName[this.normalizeLookupKey(u.name)] = u;
        });
        mscVessels.forEach((v) => {
          this.vesselsByName[this.normalizeLookupKey(v.name)] = v;
        });

        this.mscSelectOptions.mscVessels = mscVessels.map((v) => ({ id: v.id, name: v.name }));
        this.mscSelectOptions.picUsers = users.map((u) => ({ id: u.id, name: u.name }));

        MSC_SUPPLIER_SERVICE_LOADS.forEach(({ optionsKey }, idx) => {
          const res = supplierResults[idx] || {};
          const suppliers = res.suppliers || [];
          this.mscSelectOptions[optionsKey] = suppliers.map((s) => ({
            id: s.id,
            name: s.name,
          }));
        });

        this.mscEditorOptionsLoaded = true;
      }).catch(() => {});
    },
    normalizeDateCell(rawValue) {
      const value = String(rawValue || '').trim();
      if (!value) return '';
      let m = value.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})$/);
      if (m) return value;
      m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
      if (m) {
        const [, dd, mm, yyyy, hh, min] = m;
        return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
      }
      m = value.match(/^(\d{2})\/(\d{2})\s+(\d{2}):(\d{2})$/);
      if (m) {
        const year = String(new Date().getFullYear());
        const [, dd, mm, hh, min] = m;
        return `${dd}-${mm}-${year} ${hh}:${min}`;
      }
      m = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})$/);
      if (m) {
        const [, yyyy, mm, dd, hh, min] = m;
        return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
      }
      return value;
    },
    toApiDateTime(displayValue) {
      const value = String(displayValue || '').trim();
      const m = value.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})$/);
      if (!m) return null;
      const [, dd, mm, yyyy, hh, min] = m;
      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    },
    sanitizeGridText(val) {
      const s = String(val || '').trim();
      if (!s || s === '-') return null;
      return s;
    },
    applySavedRowToGrid(item, rowKey, op, vessel, user) {
      const savedJobName = op.job_name || item.job_name;
      this.$set(item, 'operation_id', op.id || item.operation_id);
      this.$set(item, 'job_name', savedJobName || '');
      this.$set(item, 'vessel_name', vessel.name);
      this.$set(item, 'pic', user.name);
      if (op.billing_party_id != null) {
        this.$set(item, 'billing_party_id', op.billing_party_id);
      }
      if (op.status != null) {
        this.$set(item, 'status', op.status);
      }
      if (this.isPoNumberBillingReady({ ...item, status: op.status || item.status })) {
        this.$set(item, 'po_number', op.po_number || '');
      }
      this.ensureTrailingBlankRows();
      if (!this.gridApi) return;
      const rowNode = this.gridApi.getRowNode(rowKey);
      if (!rowNode) return;
      rowNode.setData({ ...item });
      this.gridApi.redrawRows({ rowNodes: [rowNode] });
    },
    buildMscVesselInformation(item) {
      const berthingApi = this.toApiDateTime(item.berthing);
      const location = this.sanitizeGridText(item.location);
      if (!berthingApi && !location) return null;
      return {
        anchorage: {
          arrival_bearth: berthingApi,
          bearthing_station: location,
        },
      };
    },
    normalizeMscServiceText(val) {
      const s = String(val || '').trim();
      if (!s || s === '-') return '';
      return s;
    },
    buildMscServicesPayload(item) {
      const isExistingRow = item.operation_id && !String(item.operation_id).startsWith('new-');
      const immigration = this.normalizeMscServiceText(item.immigration);
      const portClearanceTime = this.normalizeMscServiceText(item.port_clearance_time);
      const ctm = this.normalizeMscServiceText(item.ctm);
      const crewChange = this.normalizeMscServiceText(item.crew_change);
      const sscecSupplier = this.normalizeMscServiceText(item.sscec_supplier);
      const garbageDisposalSupplier = this.normalizeMscServiceText(item.garbage_disposal_supplier);
      const hotelSignOnSuppliers = this.normalizeMscServiceText(item.hotel_booking_crew_sign_on_supplier);
      const hotelSignOffSuppliers = this.normalizeMscServiceText(item.hotel_booking_crew_sign_off_supplier);
      const transportServiceSuppliers = this.normalizeMscServiceText(item.transport_service_supplier);
      const othersSuppliers = this.normalizeMscServiceText(item.others_suppliers);
      const payload = {};
      if (immigration || isExistingRow) payload.immigration = immigration;
      if (portClearanceTime) payload.port_clearance_time = portClearanceTime;
      if (ctm || isExistingRow) payload.ctm = ctm;
      if (crewChange || isExistingRow) payload.crew_change = crewChange;
      if (sscecSupplier || isExistingRow) payload.sscec_supplier = sscecSupplier;
      if (garbageDisposalSupplier || isExistingRow) payload.garbage_disposal_supplier = garbageDisposalSupplier;
      if (hotelSignOnSuppliers || isExistingRow) {
        payload.hotel_booking_crew_sign_on_supplier = hotelSignOnSuppliers;
      }
      if (hotelSignOffSuppliers || isExistingRow) {
        payload.hotel_booking_crew_sign_off_supplier = hotelSignOffSuppliers;
      }
      if (transportServiceSuppliers || isExistingRow) {
        payload.transport_service_supplier = transportServiceSuppliers;
      }
      if (othersSuppliers || isExistingRow) payload.others_suppliers = othersSuppliers;
      return Object.keys(payload).length ? payload : null;
    },
    ensureTrailingBlankRows() {
      const unusedBlankCount = this.rowData.filter(
        (row) => this.isBlankRow(row) && !this.blankRowHasContent(row)
      ).length;
      const needed = MIN_TRAILING_BLANK_ROWS - unusedBlankCount;
      if (needed <= 0) return;

      const newRows = this.blankMscRows(needed);
      this.rowData.push(...newRows);
      if (this.gridApi) {
        this.gridApi.applyTransaction({ add: newRows });
      }
    },
    saveMscInlineRow(item) {
      const rowKey = String(item._gridRowKey || item.operation_id);
      const persistRow = () => {
        const rowNode = this.gridApi && this.gridApi.getRowNode(rowKey);
        const row = (rowNode && rowNode.data) || item;
        const vessel = this.vesselsByName[this.normalizeLookupKey(row.vessel_name)];
        const user = this.usersByName[this.normalizeLookupKey(row.pic)];
        const eta = this.toApiDateTime(row.eta);
        const etd = this.toApiDateTime(row.etd);
        if (!vessel || !user || !eta || !etd) {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: 'Please fill Vessel Name, PIC, ETA and ETD (dd-mm-yyyy HH:mm) before saving.',
          });
          return;
        }

        this.$set(this.savingRows, rowKey, true);
        if (this.gridApi) {
          this.gridApi.refreshCells({ columns: ['_actions'], force: true });
        }

        const billing = (vessel.card_id || [])[0];
        const isNewRow = String(row.operation_id).startsWith('new-');
        const operationDetails = {
          id: isNewRow ? null : row.operation_id,
          vessel_id: vessel.id,
          card_id: billing && billing.id,
          billing_party_id: row.billing_party_id || (billing && billing.id),
          supervisor_id: user.id,
          eta,
          etd,
          job_scope: 'Full Agent',
          status: isNewRow ? 'Open' : (row.status != null && row.status !== '' ? row.status : 'Open'),
          is_draft: false,
          job_start_date: eta.split(' ')[0],
          remarks_arrival: this.sanitizeGridText(row.remarks_arrival),
          remarks_departure: this.sanitizeGridText(row.remarks_departure),
        };
        if (!isNewRow && this.isPoNumberBillingReady(row)) {
          const poNumber = this.getRowFieldValue(rowKey, 'po_number', row);
          operationDetails.po_number = poNumber || null;
        }

        const vesselInformation = this.buildMscVesselInformation(row);
        const mscServices = this.buildMscServicesPayload(row);
        const savePayload = {
          operation_details: operationDetails,
          vessel_information: vesselInformation || {},
        };
        if (mscServices) {
          savePayload.msc_services = mscServices;
        }

        this.$api.operations.create(savePayload).then((res) => {
          const op = res.operation || {};
          this.applySavedRowToGrid(row, rowKey, op, vessel, user);
          this.$store.dispatch('setAlert', {
            status: 'success',
            show: true,
            message: `MSC row ${row.job_name || ''} saved successfully`,
          });
        }).catch((err) => {
          this.$store.dispatch('setAlert', {
            status: 'error',
            show: true,
            message: err.message,
          });
        }).finally(() => {
          this.$set(this.savingRows, rowKey, false);
          if (this.gridApi) {
            const savedRowNode = this.gridApi.getRowNode(rowKey);
            if (savedRowNode) {
              this.gridApi.refreshCells({
                rowNodes: [savedRowNode],
                columns: ['_actions'],
                force: true,
              });
            }
          }
        });
      };

      if (this.gridApi) {
        this.gridApi.stopEditing();
        this.$nextTick(persistRow);
        return;
      }
      persistRow();
    },
  },
  created() {
    this.getReports();
  },
};
</script>

<style scoped>
.msc-ag-grid-wrap {
  width: 100%;
}

.msc-ag-grid-wrap ::v-deep .ag-root-wrapper {
  border-radius: 4px;
}

.msc-ag-grid-wrap ::v-deep .ag-cell-inline-editing {
  overflow: visible;
  padding: 0 4px;
  z-index: 2;
}

.msc-ag-grid-wrap ::v-deep .ag-row-focus {
  z-index: 1;
}

.msc-ag-grid-wrap ::v-deep .ag-cell[col-id="_actions"] {
  overflow: visible !important;
  padding: 0 4px;
}
</style>

<style>
.msc-actions-menu {
  z-index: 10 !important;
}
</style>
